import { countryCodeFromIDOrCode } from "Utilities/Address/networkCountryOptions";

/**
 * Serialize an address hook value into the plain object the legacy Simple Signup
 * bulk save expects for one extended-field response — the shape
 * `formObject.getPostData()` would have produced for the legacy address widget,
 * plus the verification verdict fields.
 *
 * The verification encoding mirrors the canonical logic in
 * `src/ExtendedFields/API/queryHooks.js` (saveField's `case "address"`) and its
 * FormData sibling `src/IIGAddress/appendAddressToFormData.js` — kept
 * deliberately identical to those. This surface has no legacy verify-flow, so
 * the `options.verification` / `allowChangeCountry` branches are dropped;
 * everything else matches. The one addition over the FormData sibling is
 * `networkFieldID`, which the shared bulk loop keys each response by.
 *
 * @param {object} address       hook value shape (addr1/city/state/zip/country/...)
 * @param {number} networkFieldID
 * @returns {object} the `{ extendedFieldsData: [...] }` entry for this field
 */
export default function addressToBulkFieldData(address, networkFieldID) {
  const cleaned = Object.entries(address).reduce((acc, [key, val]) => {
    if (val != null) acc[key] = val;
    return acc;
  }, {});

  cleaned.networkFieldID = networkFieldID;

  // A non-US address carries NO verification verdict — the backend holds null
  // for every non-US address. Omit the verdict fields so the POST preserves
  // that null rather than manufacturing an "unverified" verdict.
  const isUSAddress =
    countryCodeFromIDOrCode(address?.country || address?.countryID) === "US";
  if (!isUSAddress) {
    delete cleaned.isVerified;
    delete cleaned.verificationProvider;
    delete cleaned.verifiedAt;
    return cleaned;
  }

  let isVerified;
  if (cleaned.isVerified === true) {
    isVerified = true;
  } else {
    isVerified = false;
    delete cleaned.verificationProvider;
    delete cleaned.verifiedAt;
  }
  // jQuery stringifies primitives on the wire, and PHP's `(bool)` reads the
  // string "false" as truthy — so encode as "1"/"0". The in-memory contract
  // stays boolean everywhere else.
  cleaned.isVerified = isVerified ? "1" : "0";
  return cleaned;
}
